/**
 * Core types for the Document Comparison component system.
 *
 * This module provides a reusable multi-column document viewer with
 * bounding box highlighting for AI extraction results.
 */

// ============================================================================
// Citation Types (from Extend AI)
// ============================================================================

/**
 * A point in the polygon, using pixel coordinates relative to the document.
 */
export interface PolygonPoint {
  x: number;
  y: number;
}

/**
 * Citation from Extend AI extraction metadata.
 * Represents where in the document a value was extracted from.
 */
export interface Citation {
  /** Page number (1-indexed) */
  page: number;
  /** The text that was referenced/extracted */
  referenceText?: string;
  /** Polygon points defining the bounding area (pixel coordinates) */
  polygon: PolygonPoint[];
  /** Page width in pixels (for coordinate conversion) */
  pageWidth?: number;
  /** Page height in pixels (for coordinate conversion) */
  pageHeight?: number;
}

/**
 * Confidence scores for an extracted field.
 */
export interface ConfidenceScores {
  /** Confidence from OCR processing (0-1) */
  ocrConfidence?: number | null;
  /** Confidence from model logprobs (0-1) */
  logprobsConfidence?: number | null;
}

/**
 * An extracted field with its value, metadata, and citations.
 */
export interface ExtractedField {
  /** JSON path to the field, e.g., "line_items[0].quantity" */
  path: string;
  /** Human-readable label for the field */
  label: string;
  /** The extracted value */
  value: unknown;
  /** Confidence scores for this extraction */
  confidence?: ConfidenceScores;
  /** Citations showing where this value was found in the document */
  citations: Citation[];
}

// ============================================================================
// Page & Coordinate Types
// ============================================================================

/**
 * Dimensions of a document page in pixels.
 */
export interface PageDimensions {
  width: number;
  height: number;
}

/**
 * A highlight area using percentage-based coordinates for rendering.
 * Used for positioning SVG overlays on the document.
 */
export interface HighlightArea {
  /** Left position as percentage (0-100) */
  left: number;
  /** Top position as percentage (0-100) */
  top: number;
  /** Width as percentage (0-100) */
  width: number;
  /** Height as percentage (0-100) */
  height: number;
  /** Page index (0-indexed for react-pdf compatibility) */
  pageIndex: number;
}

// ============================================================================
// Extraction Metadata Types
// ============================================================================

/**
 * Metadata for a single extracted field from Extend AI.
 */
export interface ExtendAIFieldMetadata {
  ocrConfidence?: number | null;
  logprobsConfidence?: number | null;
  citations?: Array<{
    page?: number;
    referenceText?: string;
    polygon?: PolygonPoint[];
    pageWidth?: number;
    pageHeight?: number;
  }>;
}

/**
 * The complete extraction output from Extend AI.
 */
export interface ExtendAIExtractionOutput {
  /** The extracted values */
  value: Record<string, unknown>;
  /** Metadata keyed by field path (e.g., "line_items[0].quantity") */
  metadata: Record<string, ExtendAIFieldMetadata>;
}

// ============================================================================
// Context State Types
// ============================================================================

/**
 * State managed by the DocumentComparisonProvider.
 */
export interface DocumentComparisonState {
  /** Currently hovered field path (from data panel) */
  hoveredFieldPath: string | null;
  /** Currently hovered citation (from document) */
  hoveredCitation: Citation | null;
  /** Field path that is locked (persisted highlight) */
  lockedFieldPath: string | null;
  /** Active page per document column (1-indexed) */
  activePages: Record<string, number>;
  /** Page dimensions per document column */
  pageDimensions: Record<string, Record<number, PageDimensions>>;
}

/**
 * Actions available in the DocumentComparisonProvider context.
 */
export interface DocumentComparisonActions {
  /** Set the hovered field path (from data panel hover) */
  setHoveredField: (path: string | null) => void;
  /** Set the hovered citation (from document hover) */
  setHoveredCitation: (citation: Citation | null) => void;
  /** Toggle or set the locked field path */
  setLockedField: (path: string | null) => void;
  /** Navigate to a specific page in a document column */
  navigateToPage: (columnId: string, page: number) => void;
  /** Store page dimensions for a document */
  setPageDimensions: (
    columnId: string,
    page: number,
    dimensions: PageDimensions,
  ) => void;
  /** Get the field path associated with a citation */
  getFieldForCitation: (citation: Citation) => string | null;
  /** Get citations for a field path */
  getCitationsForField: (fieldPath: string) => Citation[];
  /** Get confidence scores for a field path */
  getConfidenceForField: (fieldPath: string) => ConfidenceScores | null;
}

/**
 * Complete context value including state and actions.
 */
export interface DocumentComparisonContextValue
  extends DocumentComparisonState,
    DocumentComparisonActions {}

// ============================================================================
// Highlight State Types
// ============================================================================

/**
 * Visual state for a bounding box highlight.
 */
export type HighlightState = 'default' | 'hovered' | 'locked';

/**
 * Confidence level derived from scores.
 */
export type ConfidenceLevel = 'high' | 'medium' | 'low' | 'unknown';

/**
 * Get confidence level from scores.
 */
export function getConfidenceLevel(scores?: ConfidenceScores): ConfidenceLevel {
  if (!scores) return 'unknown';

  const combined = Math.min(
    scores.ocrConfidence ?? 1,
    scores.logprobsConfidence ?? 1,
  );

  if (combined >= 0.9) return 'high';
  if (combined >= 0.7) return 'medium';
  return 'low';
}

/**
 * Get combined confidence value (0-1).
 */
export function getCombinedConfidence(
  scores?: ConfidenceScores,
): number | null {
  if (!scores) return null;
  if (
    scores.ocrConfidence === undefined &&
    scores.logprobsConfidence === undefined
  ) {
    return null;
  }
  return Math.min(scores.ocrConfidence ?? 1, scores.logprobsConfidence ?? 1);
}
